/**
 * Portfolio page showcasing client partnerships and services
 * Updated with full 81 clients and fixed image upload functionality with persistence
 */

import React from 'react'
import { Link } from 'react-router'
import { Button } from '../components/ui/button'
import { ExternalLink, Building2, Users, Target } from 'lucide-react'
import { clientsData } from '../data/clientsData'

/**
 * PortfolioPage component
 * Renders the clients portfolio grid, statistics and CTA — accessible logo rendering and links to client work details
 */
export default function PortfolioPage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-900 via-orange-800 to-orange-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Our Clients</h1>
          <p className="text-xl text-orange-100 max-w-3xl mx-auto leading-relaxed mb-8">
            Trusted partnerships with leading brands across industries. 
            We've delivered exceptional results for over 80 clients through strategic marketing solutions.
          </p>
        </div>
      </section>

      {/* Client Grid Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Client Portfolio</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover the diverse range of brands we've helped achieve their marketing goals
            </p>
            <div className="mt-4 text-lg text-orange-600 font-semibold">
              Total Clients: {clientsData.length}
            </div>
          </div>

          {/* Client Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-6">
            {clientsData.map((client, index) => (
              <div key={client.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden hover:scale-105">
                {/* Client Logo/Image */}
                <div className="h-40 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
                  <img
                    src={client.logo}
                    alt={client.description ? client.description : `${client.name} logo`}
                    title={client.description ? client.description : `${client.name} logo`}
                    className="max-h-24 max-w-32 object-contain rounded-lg"
                    onError={(e) => {
                      // Fallback to generic company image if specific logo fails
                      e.currentTarget.src = 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/266987bf-c3f8-4c4b-98fa-02f33d6e57a7.png'
                    }}
                  />
                </div>
                
                {/* Client Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2 h-12 flex items-center justify-center text-center">
                    {client.name}
                  </h3>
                  
                  {/* Services List */}
                  <div className="space-y-1 mb-4 min-h-20">
                    {client.services.slice(0, 3).map((service, serviceIndex) => (
                      <div key={serviceIndex} className="flex items-center text-xs text-gray-600">
                        <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-2 flex-shrink-0"></div>
                        <span className="truncate">{service}</span>
                      </div>
                    ))}
                    {client.services.length > 3 && (
                      <div className="text-xs text-gray-500 text-center">
                        +{client.services.length - 3} more services
                      </div>
                    )}
                    {client.services.length === 0 && (
                      <div className="text-xs text-gray-400 text-center py-2">Services to be added</div>
                    )}
                  </div>

                  {/* Work Details Link */}
                  <Button 
                    asChild 
                    variant="outline" 
                    className="w-full bg-transparent border-orange-200 text-orange-700 hover:bg-orange-50 text-xs py-1 h-8"
                  >
                    <Link to={`/portfolio/${client.id}`}>
                      View Work Details
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Stats Section */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="bg-orange-50 p-8 rounded-2xl">
              <Building2 className="h-12 w-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-3xl font-bold text-gray-900 mb-2">{clientsData.length}+</h3>
              <p className="text-gray-600">Satisfied Clients</p>
            </div>
            
            <div className="bg-orange-50 p-8 rounded-2xl">
              <Target className="h-12 w-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-3xl font-bold text-gray-900 mb-2">15+</h3>
              <p className="text-gray-600">Years of Experience</p>
            </div>
            
            <div className="bg-orange-50 p-8 rounded-2xl">
              <Users className="h-12 w-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-3xl font-bold text-gray-900 mb-2">1000+</h3>
              <p className="text-gray-600">Campaigns Delivered</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-900 to-orange-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">Why Choose Us?</h2>
          <p className="text-lg text-orange-100 mb-8">
            By leveraging a combination of data-driven insights, industry expertise, media planning and monitoring tools alongside with a keen understanding of emerging trends, we craft bespoke media plans tailored to your specific objectives. Our aim is to maximize the impact of your message while optimizing your advertising budget for a better ROI.
          </p>
          <Button asChild size="lg" className="bg-white text-orange-700 hover:bg-orange-50 text-lg px-8">
            <Link to="/contact">Start Your Project</Link>
          </Button>
        </div>
      </section>
    </div>
  )
}